import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import * as Bixby from '$/core';
import SortFilter, { ISortItem } from '$/components/sortFilter';
import ResultsCounter from '$/components/resultsCounter';
import { IFilterItem } from '$/components/sortFilter/filterItem';
import NewsItem from '$/components/newsItem';
import PrintPage from '$/components/printPage';
import Loading from '$/components/loading/Loading';
import { AnalyticsService } from '$/services';
import './index.scss';

interface INewsEarningsProps {
  filters: IFilterItem[];
  settings?: Bixby.Models.Settings;
  companyId?: string;
  companyName?: string;
  dataSiteName?: string;
  isClient?: boolean;
  hideCompanyLink?: boolean;
  hidePrint?: boolean;
  hideSortFilter?: boolean;
  profile?: Bixby.Models.Profile;
  getNews: () => Promise<Bixby.Models.News[]>;
  getNewsEntry: (id: string) => Promise<Bixby.Models.News>;
}

export const NewsEarnings: React.FC<INewsEarningsProps> = props => {
  const {
    filters,
    settings,
    companyId,
    companyName,
    dataSiteName,
    isClient,
    hideCompanyLink,
    hidePrint,
    hideSortFilter,
    profile,
    getNews,
    getNewsEntry,
  } = props;

  const [newsError, setNewsError] = useState(null as boolean | null);
  const [isLoading, setIsLoading] = useState(null as boolean | null);
  const [sorts, setSorts] = useState([] as Array<ISortItem<any>>);
  const [activeSort, setActiveSort] = useState(Bixby.Enums.NewsEarningsSort.All as Bixby.Enums.NewsEarningsSort);
  const [filterTypes] = useState(filters as Array<IFilterItem>);
  const [activeFilterTypes, setActiveFilterTypes] = useState([] as Array<string>);
  const [news, setNews] = useState([] as Array<Bixby.Models.News>);
  const [filteredNewsEarnings, setFilteredNewsEarnings] = useState(undefined as Array<Bixby.Models.News> | undefined);
  const [getNewsLocal] = useState(getNews as () => Promise<Bixby.Models.News[]>);

  useEffect(() => {
    if (settings) {
      const initial: Array<ISortItem<any>> = [
        { name: 'View All News', value: Bixby.Enums.NewsEarningsSort.All },
        { name: `Last ${settings.newsCutoff} days`, value: Bixby.Enums.NewsEarningsSort.LastXDays },
      ];
      setSorts(initial);
    }
  }, [settings]);

  // Original implementation has all the filtering and sorting done in-memory
  // Eventually we can refactor this to fetch incrementally
  useEffect(() => {
    const getNewsAsync = async () => {
      setNewsError(false);
      setIsLoading(true);
      try {
        const result: Bixby.Models.News[] = await getNewsLocal;
        setNews(result);
        setFilteredNewsEarnings(result || []);
      } catch (e: any) {
        setNewsError(true);
      } finally {
        setIsLoading(false);
      }
    };

    getNewsAsync();
  }, [getNewsLocal]);

  const handleFilter = (id: string, isFilterActive: boolean) => {
    if (isFilterActive) {
      setActiveFilterTypes([...activeFilterTypes, id]);
    } else {
      const indexOfFilter = activeFilterTypes.findIndex(_ => _ === id);
      if (indexOfFilter > -1) {
        activeFilterTypes.splice(indexOfFilter, 1);
        setActiveFilterTypes([...activeFilterTypes]);
      }
    }
  };

  const handleClearFilters = () => {
    setActiveFilterTypes([]);
  };

  const handleSort = (_event: any, id: Bixby.Enums.NewsEarningsSort) => {
    setActiveSort(id);
  };

  useEffect(() => {
    let result: Array<Bixby.Models.News> = news;

    // get news by sort
    if (settings && activeSort == Bixby.Enums.NewsEarningsSort.LastXDays) {
      const daysAgo = dayjs().subtract(settings.newsCutoff, 'days');
      result = news.filter(_ => Bixby.Utils.utcDateAsLocal(_.date).isAfter(daysAgo, 'days'));
    }

    // filter news
    result =
      activeFilterTypes.length > 0
        ? result.filter(n => activeFilterTypes.findIndex(f => f === n.newsTypeId) > -1)
        : result;

    setFilteredNewsEarnings(result);
  }, [news, settings, activeSort, activeFilterTypes]);

  let newsFeedbackUrl = '';

  if (isClient && companyId) {
    const companyNameSanitized: string = (companyName || '').replace('&', '%26');
    const dataSiteNameSanitized: string = (dataSiteName || '').replace('&', '%26');

    const newsFeedbackUrlSubject = `News%20Request%20-%20${companyNameSanitized}`;
    const dataSite: string = dataSiteNameSanitized ? `%20%28${dataSiteNameSanitized}%29` : '';
    const newsFeedbackUrlBody = `Please%20add%20news%20for%20${companyNameSanitized}${dataSite}%2c%20and%20include%20any%20specific%20requests%20here%3A%0D%0A%0D%0A`;
    newsFeedbackUrl = `mailto:feedback@bixbyresearch.com?subject=${newsFeedbackUrlSubject}&body=${newsFeedbackUrlBody}`;
  }

  const handleFeedbackClick = () => {
    if (profile) {
      AnalyticsService.event(Bixby.Constants.APPINSIGHTS_FEEDBACK_LINK_CLICK, {
        ...profile,
        type: 'News',
        dataSiteName: dataSiteName,
        companyName: companyName,
      });
    }
  };

  if (newsError) {
    return (
      <section>
        <div className="news-note">
          <p className="error">Error retrieving news</p>
        </div>
      </section>
    );
  } else if (isLoading || !filteredNewsEarnings) {
    return <Loading pageLevel />;
  } else if (filteredNewsEarnings) {
    return (
      <div>
        {hideSortFilter !== true && (
          <SortFilter
            filters={filterTypes}
            sorts={sorts}
            multiselect={true}
            onFilter={handleFilter}
            onClearFilters={handleClearFilters}
            onSort={handleSort}
          />
        )}

        {hidePrint !== true && <PrintPage />}

        <ResultsCounter amount={(filteredNewsEarnings || []).length} />

        {newsFeedbackUrl && (
          <p className="news_feedback">
            Don't see all the news you're looking for?{' '}
            <a href={newsFeedbackUrl} onClick={handleFeedbackClick}>
              Let us know
            </a>{' '}
            and we can populate historical news.
          </p>
        )}

        {(filteredNewsEarnings || []).map((n, ix) => {
          return (
            <NewsItem
              key={ix + '-' + n.id}
              id={n.id}
              category={n.newsTypeName}
              title={n.subject}
              date={n.date}
              aboutUrl={`/company/${n.companyId}`}
              hideAboutUrl={hideCompanyLink}
              aboutTitle={n.companyName}
              description={n.description}
              getNewsEntry={getNewsEntry}
            />
          );
        })}
      </div>
    );
  } else {
    return null;
  }
};
